import { useState, useEffect } from 'react';
import Image from 'next/image';
import beTwMerge from '@/utils/beTwMerge';
import { IS_DEV } from '@/utils/env';
import { dataVirtual } from '@/utils/constants';
import useStore from '@/store/useStore';
import useCoordinates from '@/hooks/useCoordinates';
import type { IDataShowroom } from '@/components/BuilderIO/common/CommonFields';
import { setsterServices, setsterServicesDateNight } from '@/consts/appointment';
import Button from '@/components/homepage/Button';
import { LoadingSpinner } from '@/components/Loading';

// This is the main component for the Service Page in the Appointment Booking Modal.
// It handles the selection of services, setting of information, and updating of states.
export default function PageOne({
  user,
  info,
  builderShowroomsCopy,
  setBuilderShowroomsCopy,
  setInfo,
}: {
  user: any;
  info: any;
  builderShowroomsCopy: IDataShowroom[];
  setBuilderShowroomsCopy: React.Dispatch<React.SetStateAction<IDataShowroom[]>>;
  setInfo: (key: string, value: any) => void;
}) {
  // Get the function to fetch availability date times from the store
  const getSetsterAvailabilityDateTimes = useStore(state => state.getSetsterAvailabilityDateTimes);
  const getSetsterAvailabilityDateNightTimes = useStore(state => state.getSetsterAvailabilityDateTimes);
  const [hasDateNight, setHasDateNight] = useState<boolean>(false);
  // Get the ID for the virtual appointment
  const virtualAppointmentID = dataVirtual.id;

  // Define the services available
  const services = [
    {
      imgSrc: '/bee2-ui/img/testing/service-one.jpg',
      cityBackgroundStyle:
        'url("/bee2-ui/img/testing/service-one.jpg") lightgray -76.045px -19.08px / 289.43% 192.977% no-repeat',
      virtualBackgroundStyle:
        'url("/bee2-ui/img/testing/service-one.jpg") lightgray -93.427px -23.442px / 289.43% 192.977% no-repeat',
      id: IS_DEV ? '56861' : '38158',
      title: 'Engagement Ring',
      description: 'Find your ideal ring setting and center diamond or gemstone pairing.',
      time: '50 minutes',
    },
    {
      imgSrc: '/bee2-ui/img/testing/service-two.jpg',
      cityBackgroundStyle:
        'url("/bee2-ui/img/testing/service-two.jpg") lightgray -36.625px -8.651px / 195.349% 143.681% no-repeat',
      virtualBackgroundStyle:
        'url("/bee2-ui/img/testing/service-two.jpg") lightgray -44.996px -10.629px / 195.349% 143.681% no-repeat',
      id: IS_DEV ? '56862' : '38221',
      title: 'Wedding Ring',
      description: 'Our experts will help you and your partner select the perfect wedding bands.',
      time: '50 Minutes',
    },
    {
      imgSrc: '/bee2-ui/img/testing/service-three.jpg',
      cityBackgroundStyle:
        'url("/bee2-ui/img/testing/service-three.jpg") lightgray -94.469px -52.351px / 376.885% 216.999% no-repeat',
      virtualBackgroundStyle:
        'url("/bee2-ui/img/testing/service-three.jpg") lightgray -116.062px -64.316px / 376.885% 216.999% no-repeat',
      id: IS_DEV ? '56863' : '38313',
      title: 'Fine Jewelry & Gifts',
      description: 'Discover our assortment of necklaces, bracelets, earrings, and more, for gifting—or wearing.',
      time: '50 Minutes',
    },
    {
      imgSrc: null,
      id: IS_DEV ? '56865' : '43035',
      title: 'Drop Off - Resize, Service, Return',
      time: '15 minutes',
      description: 'Leave your item with us for servicing, or make a return.',
    },
    {
      imgSrc: null,
      id: IS_DEV ? '56864' : '43034',
      title: 'Pick Up - Orders, Resize, Service',
      time: '15 minutes',
      description: 'Come by and collect your order or serviced item.',
    },
  ];
  // Determine if the user is in a city or not
  const isCity = info.selectedShowroomStore.modalName !== 'Virtual Appointment';
  // Initialize the services copy state
  const [servicesCopy, setServices] = useState(isCity ? services : services.slice(0, 3));
  const [loading, setLoading] = useState(true);

  // This will return the current date in the format YYYY-MM-DD
  function getTodayDate() {
    const today = new Date();
    const offset = today.getTimezoneOffset();
    const todayDate = new Date(today.getTime() - offset * 60 * 1000);
    const result = todayDate.toISOString().split('T')[0];

    return result;
  }

  const getServiceDateNightId = (serviceId: string) => {
    if (serviceId === setsterServices.engagementRing) return setsterServicesDateNight.dateNightER;
    if (serviceId === setsterServices.weddingRing) return setsterServicesDateNight.dateNightWB;
    if (serviceId === setsterServices.fineJewelryGifts) return setsterServicesDateNight.dateNightFJ;
    return '';
  };

  // Handle the click event when a service is selected
  const handleServiceClick = async (service, idx) => {
    try {
      setInfo('setsterAvailabilityDateTimes', {});
      if (idx !== 3 && idx !== 4 && hasDateNight) {
        await getSetsterAvailabilityDateNightTimes(
          getServiceDateNightId(service.id),
          info.selectedShowroomStore.id,
          getTodayDate(),
          '0'
        );
      }

      getSetsterAvailabilityDateTimes(
        service.id,
        info.selectedShowroomStore.id,
        getTodayDate(),
        // eslint-disable-next-line no-nested-ternary
        info.selectedShowroomStore.modalName === 'Virtual Appointment'
          ? info.selectedTimezoneId !== '0'
            ? info.selectedTimezoneId
            : '553'
          : '0'
      );

      // Clear date & time if a different service is selected
      if (service?.id !== info?.selectedServiceId) {
        setInfo('selectedTime', null);
        setInfo('selectedDate', null);
        setInfo('selectedTimeId', null);
      }

      (window as any).dataLayer.push({
        event: 'appointment_form',
        showroom_page_section: 'select a service',
        showroom_action: service.title,
        showroom_location: info.prevSelectedShowroomStore.modalName,
        general_event_details: 'Undefined',
      });

      if (info.selectedShowroomStore.modalName === 'Virtual Appointment') {
        setInfo('selectedTimezoneId', info.selectedTimezoneId !== '0' ? info.selectedTimezoneId : '553');
      }

      if (idx === 3) {
        setInfo('selectedServiceId', service.id);
        setInfo('selectedServiceName', 'Drop Off');
      } else if (idx === 4) {
        setInfo('selectedServiceId', service.id);
        setInfo('selectedServiceName', 'Pick Up');
      } else {
        setInfo('selectedServiceId', service.id);
        setInfo('selectedServiceDateNightId', getServiceDateNightId(service.id));
        setInfo('selectedServiceName', service.title);
      }
      setInfo('activePage', info.activePage + 1);
    } catch (error: any) {
      setInfo('isSetsterError', true);
    }
  };

  // Get the filtered showrooms from the store
  const filteredShowrooms = useStore(state => state.filteredAppointmentModalShowrooms);
  // Get the function to set near showrooms from the store
  const setNearShowrooms = useStore(state => state.setNearAppointmentModalShowrooms);
  // Get the function to set geolocation from the store
  const setGeolocation = useStore(state => state.setGeolocation);
  // Initialize the first loaded state
  const [firstLoaded, setFirstLoaded] = useState(false);

  // Function to calculate initial coordinates
  function calculateInitialCoords() {
    if (!user.ipLatitude || !user.ipLongitude) {
      setGeolocation({});
    }
  }

  // Get the coordinates from the hook
  const { getCoordinates } = useCoordinates();

  // Function to update near showrooms
  const updateNearShowrooms = async (zipcode, latitude, longitude) => {
    setInfo('locationsLoading', true);
    if (latitude && longitude) {
      const newFilteredShowrooms = await setNearShowrooms(latitude, longitude, '', '');

      setBuilderShowroomsCopy([...newFilteredShowrooms]);
    } else if (zipcode) {
      try {
        const newCoords = await getCoordinates(zipcode);

        const newFilteredShowrooms = await setNearShowrooms(newCoords?.lat, newCoords?.lng, newCoords?.state, '');

        setBuilderShowroomsCopy([...newFilteredShowrooms]);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    } else {
      try {
        const newFilteredShowrooms = await setNearShowrooms(user.ipLatitude, user.ipLongitude, null, '');

        setBuilderShowroomsCopy([...newFilteredShowrooms]);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    }
    setInfo('locationsLoading', false);
  };

  // useEffect hook to calculate initial coordinates
  useEffect(() => {
    calculateInitialCoords();
  }, []);

  useEffect(() => {
    const getDateNightTimes = async () => {
      try {
        if (Object.keys(info.setsterAvailabilityDateNightTimes).length) {
          setHasDateNight(true);
          setLoading(false);
          return;
        }

        if (info.selectedShowroomStore.id) {
          const dateNightIds = [
            setsterServicesDateNight.dateNightER,
            setsterServicesDateNight.dateNightWB,
            setsterServicesDateNight.dateNightFJ,
          ];
          const promises = dateNightIds.map(async dateNightId => {
            const dateNightTimes = await getSetsterAvailabilityDateNightTimes(
              dateNightId,
              info.selectedShowroomStore.id,
              getTodayDate(),
              '0'
            );
            return dateNightTimes;
          });

          const dateNightTimes = await Promise.all(promises);
          setHasDateNight(dateNightTimes.some(dateNightTime => Object.keys(dateNightTime).length > 0));
        }
        setLoading(false);
        setInfo('isSetsterError', false);
      } catch (error) {
        setInfo('isSetsterError', true);
        setLoading(false);
      }
    };
    getDateNightTimes();
  }, [info.selectedShowroomStore.id]);

  // useEffect hook to update near showrooms
  useEffect(() => {
    if (!firstLoaded) {
      // isVA = is Virtual Appointment
      const isVA = info.selectedShowroomStore.modalName === 'Virtual Appointment';

      const zipCode = isVA ? null : info.selectedShowroomStore.zipCode;
      const latitude = isVA ? user.ipLatitude : info.selectedShowroomStore.latitude;
      const longitude = isVA ? user.ipLongitude : info.selectedShowroomStore.longitude;

      zipCode ? updateNearShowrooms(zipCode, latitude, longitude) : updateNearShowrooms(null, latitude, longitude);
    }

    setFirstLoaded(true);
  }, [info.selectedShowroomStore]);

  // useEffect hook to update builder showrooms copy
  useEffect(() => {
    if (filteredShowrooms && filteredShowrooms.length > 0 && builderShowroomsCopy.length === 0) {
      setBuilderShowroomsCopy([...filteredShowrooms]);
    }
  }, [filteredShowrooms, builderShowroomsCopy, setBuilderShowroomsCopy]);

  const renderServices = () => {
    if (info.isSetsterError)
      return (
        <div className="tw-col-span-2 tw-max-w-[670px] tw-text-sm tw-text-center lg:tw-text-lg tw-text-[#C21419]">
          We&#39;re having trouble loading available times right now. You can try again in a moment, or&nbsp;
          <a className="tw-underline !tw-text-[#C21419]" href="/contact">
            contact us
          </a>
          &nbsp;and we&#39;ll be happy to help you schedule.
        </div>
      );
    return (
      <>
        {servicesCopy.map((service, idx) => (
          <div
            key={service.title.split(' ').join('-')}
            role="button"
            tabIndex={0}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                handleServiceClick(service, idx);
              }
            }}
            className={beTwMerge(
              'tw-p-2 tw-cursor-pointer tw-border-solid !tw-border-[1.5px] tw-border-[#D9D9D9] tw-flex tw-gap-x-4 hover:tw-border-color-light-green-cta tw-items-center',
              idx >= 3 ? 'lg:tw-col-span-1 tw-p-4' : 'lg:tw-col-span-2',
              service.id === info.selectedServiceId && 'tw-border-color-light-green-cta !tw-border-[1.5px] tw-p-[6px]'
            )}
            onClick={() => {
              handleServiceClick(service, idx);
            }}
          >
            {service.imgSrc !== null && (
              <div
                className={beTwMerge(
                  'tw-aspect-square tw-relative',
                  isCity ? 'tw-w-[70px] tw-max-h-[70px]' : 'tw-w-[86px] tw-max-h-[86px]'
                )}
              >
                <Image fill src={service.imgSrc} alt={service.title} />
              </div>
            )}

            <div
              className={beTwMerge(
                'tw-flex tw-flex-col tw-gap-y-[2px] tw-justify-center',
                // eslint-disable-next-line no-nested-ternary
                idx >= 3
                  ? 'tw-flex-[0_0_100%]'
                  : !isCity
                    ? '!tw-flex-[0_0_calc(100%-102px)]'
                    : '!tw-flex-[0_0_calc(100%-86px)]'
              )}
            >
              <h4 className="tw-font-[500] tw-text-sm tw-m-0 tw-text-be-dark-gray">{service.title}</h4>
              {!hasDateNight && (
                <span className="tw-font-[350] tw-font-book tw-text-xs tw-text-color-grey-700">{service.time}</span>
              )}
              <p className="tw-font-[350] tw-font-book tw-text-xs tw-text-color-grey-700 tw-m-0">
                {service.description}
              </p>
            </div>
          </div>
        ))}
      </>
    );
  };

  // Render the Service Page component
  return (
    <div className="tw-flex tw-flex-col tw-justify-center">
      <div className="tw-pt-4 tw-px-4 tw-flex tw-flex-col tw-h-full tw-gap-y-4 lg:tw-gap-x-0 lg:tw-max-w-[742px] lg:tw-mx-auto ">
        <h3 className="tw-uppercase tw-font-[500] tw-text-sm tw-text-center lg:tw-text-lg  tw-m-0 tw-text-be-dark-gray tw-font-[Avenir] !tw-tracking-[0.7px]  lg:!tw-tracking-[0.9px]">
          Select a service
        </h3>

        <div className="tw-flex tw-flex-col tw-gap-y-2 lg:tw-h-auto">
          <div className="tw-flex tw-flex-col lg:tw-w-[670px] tw-gap-y-2 tw-h-fit tw-max-h-[560px] lg:tw-overflow-none tw-scrollbar-none lg:tw-grid lg:tw-grid-cols-2 lg:tw-gap-x-2 lg:tw-max-h-none lg:tw-scrollbar-none">
            {loading ? (
              <div className="tw-flex tw-mt-12 tw-w-full tw-justify-center tw-col-span-2">
                <LoadingSpinner />
              </div>
            ) : (
              renderServices()
            )}
          </div>
        </div>
      </div>
      <Button
        label={
          // eslint-disable-next-line no-nested-ternary
          info.selectedShowroomStore.modalName !== 'Virtual Appointment'
            ? 'Switch to Virtual'
            : info.prevSelectedShowroomStore.modalName === 'Virtual Appointment'
              ? 'Switch to In-Person'
              : `Switch to ${info.prevSelectedShowroomStore.modalName}`
        }
        type="link"
        className="tw-text-sm tw-mt-8"
        onClick={() => {
          if (info.selectedShowroomStore.modalName === 'Virtual Appointment') {
            if (info.prevSelectedShowroomStore.modalName === 'Virtual Appointment') {
              (window as any).dataLayer.push({
                event: 'appointment_form',
                showroom_page_section: 'select a service',
                showroom_action: 'switch to in-person',
                showroom_location: 'virtual',
                general_event_details: 'Undefined',
              });
              setInfo('activePage', info.activePage - 1);
            } else {
              (window as any).dataLayer.push({
                event: 'appointment_form',
                showroom_page_section: 'select a service',
                showroom_action: `switch to showroom|${info.prevSelectedShowroomStore.modalName}`,
                showroom_location: 'virtual',
                general_event_details: 'Undefined',
              });
              setInfo('selectedShowroomStore', info.prevSelectedShowroomStore);
              setInfo('selectedTimezoneId', '0');
              setInfo('selectedTime', null);
              setInfo('selectedDate', null);
              setInfo('selectedTimeId', null);
              setServices(services);
            }
          } else {
            (window as any).dataLayer.push({
              event: 'appointment_form',
              showroom_page_section: 'select a service',
              showroom_action: `switch to virtual`,
              showroom_location: info.prevSelectedShowroomStore.modalName,
              general_event_details: 'Undefined',
            });

            setInfo('selectedShowroomStore', {
              modalName: 'Virtual Appointment',
              displayName1: 'Virtual Appointment',
              displayName2: '',
              addresLine1: '',
              addressLine2: '',
              id: virtualAppointmentID,
              setsterLocationId: 'Virtual Appointment',
              timezoneName: '',
              timezoneAbreviation: 'PT',
              city: '',
              state: '',
              zipCode: '',
            });
            setInfo('selectedTimezoneId', '0');
            setInfo('selectedTime', null);
            setInfo('selectedDate', null);
            setInfo('selectedTimeId', null);
            setServices(curr => (!isCity ? services : curr.slice(0, 3)));
          }
        }}
      />
    </div>
  );
}
